import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Shield, Copy, Check, Download, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { usersApi } from '../../utils/api';

const TwoFactorSetup = ({ isEnabled, onStatusChange }) => {
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);
  const [isBackupCodesDialogOpen, setIsBackupCodesDialogOpen] = useState(false);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleStartSetup = async () => {
    setIsLoading(true);
    try {
      const response = await usersApi.setup2FA();
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setIsSetupDialogOpen(true);
      toast.success('Scan QR code with your authenticator app');
    } catch (error) {
      console.error('Failed to start 2FA setup:', error);
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setIsCopied(true);
    toast.success('Secret copied to clipboard');
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleVerifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await usersApi.verify2FA({ code: verificationCode });
      setBackupCodes(response.data.backupCodes);
      setIsSetupDialogOpen(false);
      setIsBackupCodesDialogOpen(true);
      onStatusChange(true);
      toast.success('2FA enabled successfully!');
    } catch (error) {
      console.error('Failed to verify 2FA:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsLoading(false);
      setVerificationCode('');
    }
  };

  const handleDownloadBackupCodes = () => {
    const content = `Skillerate Backup Codes\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\nStore these codes in a safe place. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skillerate-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      await usersApi.disable2FA({ code: disableCode });
      setIsDisableDialogOpen(false);
      onStatusChange(false);
      toast.success('2FA disabled successfully');
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsLoading(false);
      setDisableCode('');
    }
  };

  return (
    <>
      {/* Main 2FA Status Card */}
      {isEnabled ? (
        <div className="max-w-md p-4 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-900 dark:text-green-100">2FA is enabled</p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Your account is protected with two-factor authentication.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDisableDialogOpen(true)}
                disabled={isLoading}
                className="mt-3 border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                Disable 2FA
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-md p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-text-dark dark:text-dark-text">Enable Two-Factor Authentication</p>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary mt-1">
                Add an extra layer of security to your account by requiring a verification code in addition to your password.
              </p>
              <Button
                variant="gradient"
                size="sm"
                onClick={handleStartSetup}
                disabled={isLoading}
                className="mt-3"
              >
                Enable 2FA
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Setup Dialog */}
      <Dialog open={isSetupDialogOpen} onOpenChange={setIsSetupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with Google Authenticator or enter the code manually
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* QR Code */}
            {qrCode && (
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>
            )}

            {/* Manual Entry */}
            <div className="space-y-2">
              <Label htmlFor="manual-secret">Or enter this code manually:</Label>
              <div className="flex gap-2">
                <Input
                  id="manual-secret"
                  value={secret}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopySecret}
                  className="shrink-0"
                >
                  {isCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Verification Input */}
            <div className="space-y-2">
              <Label htmlFor="verification-code">Enter 6-digit code</Label>
              <Input
                id="verification-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="text-center text-lg tracking-widest"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="gradient"
              onClick={handleVerifyAndEnable}
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify & Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={isBackupCodesDialogOpen} onOpenChange={setIsBackupCodesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
            <DialogDescription>
              Store these codes in a safe place. You can use them if you lose access to your authenticator app.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div key={index} className="text-center p-2 bg-background rounded">
                  {code}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadBackupCodes}
                className="flex-1"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Codes
              </Button>
              <Button
                variant="gradient"
                onClick={() => setIsBackupCodesDialogOpen(false)}
                className="flex-1"
              >
                I've Saved My Codes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={isDisableDialogOpen} onOpenChange={setIsDisableDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your authenticator code to disable 2FA
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disable-code">Authentication Code</Label>
              <Input
                id="disable-code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="text-center text-lg tracking-widest"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDisableDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={isLoading || disableCode.length !== 6}
            >
              {isLoading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TwoFactorSetup;
